import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import {
  ActionsButton,
  DataTable,
  type InferFetcherParams,
} from '@/components/ui/data-table';
import { ContentLoader } from '@/components/ui/loaders';
import { SearchToolbar } from '@/components/ui/search-toolbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { isAdminTierRole } from '@/lib/permissions';
import {
  layoutToColumns,
  layoutToFilters,
  useFilterDefaults,
} from '@/lib/screen-renderer';
import { useIsAuthenticated } from '@/services/auth';
import { useDynamicDelete, useDynamicList } from '@/services/dynamic-resource';
import { useScreenByResource, useScreenPermissions } from '@/services/screens';
import { ResetPasswordButton } from './+components/reset-password-button';

export const Route = createFileRoute('/_app/$resource/')({
  head: () => meta({ label: 'Listagem' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { resource } = Route.useParams();
  const isMobile = useIsMobile();
  const { push } = useRouter();

  const { data: screen, isLoading: loadingScreen } = useScreenByResource(resource);
  const { canCreate, canRead, canUpdate, canDelete } = useScreenPermissions(screen);
  const { user: currentUser } = useIsAuthenticated();

  const { mutateAsync: remove } = useDynamicDelete(resource);
  const useList = (params: InferFetcherParams<any>) =>
    useDynamicList(resource, params as any);

  const { data: filterDefaults, isLoading: loadingDefaults } = useFilterDefaults(
    screen?.layout.filters,
  );

  // null até o SearchToolbar aplicar os filtros iniciais (sessão ou padrão da screen) —
  // evita buscar a listagem sem filtro e logo em seguida refazer com filtro.
  // Guardado junto do resource pra não vazar a busca de uma screen pra outra ao navegar.
  const [searchState, setSearchState] = useState<{
    resource: string;
    values: Record<string, any>;
  } | null>(null);
  const search =
    searchState && searchState.resource === resource ? searchState.values : null;
  const setSearch = (values: Record<string, any>) => setSearchState({ resource, values });

  const handleAdd = () => push(`/${resource}/create`);
  const handleEdit = (id: string) => push(`/${resource}/${id}`);
  const handleView = (id: string) => push(`/${resource}/${id}/view`);
  const handleRemove = async (id: string) => await remove(id);
  const handleBulkRemove = async (ids: string[]) => {
    await Promise.all(ids.map((id) => remove(id)));
  };

  const handleDoubleClick = (row: any) =>
    canUpdate ? handleEdit(row.id) : canRead ? handleView(row.id) : undefined;

  if (loadingScreen || !screen || loadingDefaults) return <ContentLoader show />;

  const columns = layoutToColumns(
    [
      { name: 'id', label: 'Cód.', type: 'string', sortable: true },
      ...screen.layout.columns,
      { name: 'createdAt', label: 'Criado em', type: 'date', sortable: true },
      { name: 'updatedAt', label: 'Atualizado em', type: 'date', sortable: true },
    ],
    resource,
  );
  const filters = layoutToFilters(screen.layout.filters);

  columns[isMobile ? 'unshift' : 'push']({
    id: 'actions',
    header: () => 'Ações',
    enableHiding: false,
    cell: ({ row }: any) => {
      // O próprio usuário não pode editar/excluir/resetar a própria senha por aqui —
      // edição de nome/foto/senha vai pelo Editar Perfil, e ninguém deveria conseguir
      // se apagar sozinho.
      const isSelf = resource === 'users' && row.original.id === currentUser?.id;

      return (
        <div className="flex items-center justify-end gap-1">
          {resource === 'users' && isAdminTierRole(currentUser?.role) && !isSelf && (
            <ResetPasswordButton userId={row.original.id} />
          )}
          <ActionsButton
            key={row.id}
            id={row.original.id}
            edit={canUpdate && !isSelf}
            view={canRead}
            remove={canDelete && !isSelf}
            onEdit={handleEdit}
            onView={handleView}
            onRemove={handleRemove}
          />
        </div>
      );
    },
    meta: { sticky: isMobile ? 'left' : 'right' },
  });

  return (
    <Content
      breadcrumbs={{
        icon: screen.icon as IconName,
        title: screen.pluralLabel,
        action: canCreate && (
          <Button
            variant="sand"
            size="lg"
            onClick={handleAdd}
            icon="plus"
            aria-label="Adicionar"
            className="size-11 px-0 sm:w-auto sm:px-8"
          >
            <span className="hidden sm:inline">Adicionar</span>
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <SearchToolbar
        key={`toolbar-${resource}`}
        filters={filters}
        onSearch={setSearch}
        defaultValues={filterDefaults?.values}
        defaultLabels={filterDefaults?.labels}
      />
      {search && (
        <DataTable
          key={`table-${resource}`}
          columns={columns}
          useFetcher={useList as any}
          search={search}
          pageSize={screen.layout.perPage}
          onRowDoubleClick={handleDoubleClick}
          enableSelection={canDelete}
          onBulkRemove={handleBulkRemove}
        />
      )}
    </Content>
  );
}
